import { useEffect, useState } from 'react';
import { Outlet, useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { Layout as AntLayout, Grid, Spin } from 'antd';
import { Sidebar } from './Sidebar';
import { Header } from './Header';
import { Breadcrumb, BreadcrumbProvider } from './Breadcrumb';
import { GraceBanner, TrialBanner, OfflineBanner, SampleDataBanner } from '@/components/FeatureGate';
import { usePlanData } from '@/hooks/usePlanData';
import { useNotificationSocket } from '@/hooks/useNotificationSocket';
import { PosFullscreenProvider, usePosFullscreen } from '@/pages/POS/PosFullscreenContext';
import { onboardingActions, onboardingSelectors } from '@/redux/slices/onboardingSlice';
import { onboardingService } from '@/services/onboarding.service';
import { productService } from '@/services/product.service';
import { ROUTES } from '@/constants/routes';
import { shouldSkipOnboardingProgressFetch } from './onboardingProgress';

const { Content } = AntLayout;
const { useBreakpoint } = Grid;

function LayoutInner() {
  const [collapsed, setCollapsed] = useState(false);
  const [mobileSidebarOpen, setMobileSidebarOpen] = useState(false);
  const [initializing, setInitializing] = useState(true);
  const screens = useBreakpoint();
  const isMobile = !screens.lg;
  const { posAppFullscreen } = usePosFullscreen();
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const onboardingLoaded = useSelector(onboardingSelectors.loaded);
  const onboardingCompleted = useSelector(onboardingSelectors.isCompleted);
  const onboardingProgress = useSelector(onboardingSelectors.progress);
  const skipProgressFetch = shouldSkipOnboardingProgressFetch(onboardingProgress);

  // Pre-load onboarding progress from BE BEFORE any render/routing decision.
  // This blocks the UI with a Spin until Redux state is hydrated.
  useEffect(() => {
    let cancelled = false;

    if (skipProgressFetch) {
      setInitializing(false);
      return () => {
        cancelled = true;
      };
    }

    onboardingService
      .getProgress()
      .then((p) => {
        if (cancelled) return;
        dispatch(
          onboardingActions.setProgress({
            tenantId: p.tenant_id,
            tenantName: p.tenant_name,
            address: p.address,
            cityId: p.city_id,
            wardId: p.ward_id,
            phase1: {
              totalSteps: p.phase_1.total_steps,
              completedSteps: p.phase_1.completed_steps,
              skippedSteps: p.phase_1.skipped_steps,
              isCompleted: p.phase_1.is_completed,
              completedAt: p.phase_1.completed_at,
            },
            phase2: {
              totalSteps: p.phase_2.total_steps,
              completedSteps: p.phase_2.completed_steps,
              skippedSteps: p.phase_2.skipped_steps,
              isCompleted: p.phase_2.is_completed,
              completedAt: p.phase_2.completed_at,
              dismissedAt: p.phase_2.dismissed_at,
            },
          }),
        );
        if (p.phase_1.is_completed) {
          productService
            .list({ q: 'SAMPLE-RING-001', page_size: 1 })
            .then((res) => {
              if (cancelled) return;
              dispatch(onboardingActions.setHasSampleData(res.data.length > 0));
            })
            .catch(() => {});
        }
      })
      .catch(() => {
        if (cancelled) return;
        // On BE error, don't redirect to onboarding — let user continue.
        // Mark loaded with defaults so gate evaluates but doesn't block.
        dispatch(
          onboardingActions.setProgress({
            phase1: {
              totalSteps: 0,
              completedSteps: [],
              skippedSteps: [],
              isCompleted: true, // Assume completed on error to avoid redirect loop
              completedAt: null,
            },
            phase2: {
              totalSteps: 0,
              completedSteps: [],
              skippedSteps: [],
              isCompleted: true,
              completedAt: null,
              dismissedAt: null,
            },
          }),
        );
      })
      .finally(() => {
        if (!cancelled) setInitializing(false);
      });
    return () => {
      cancelled = true;
    };
  }, [dispatch, skipProgressFetch]);

  // Load + sync plan info to Redux once user is authenticated
  usePlanData();
  // Realtime notification subscription — graceful degrade nếu BE chưa wire WS
  useNotificationSocket();

  // Gate: nếu onboarding chưa hoàn thành → redirect về onboarding wizard
  // Chỉ fire SAU khi initializing=false (Redux đã hydrated)
  useEffect(() => {
    if (initializing) return;
    if (onboardingLoaded && !onboardingCompleted) {
      navigate(ROUTES.onboarding.index, { replace: true });
    }
  }, [initializing, onboardingLoaded, onboardingCompleted, navigate]);

  useEffect(() => {
    if (!isMobile) {
      setMobileSidebarOpen(false);
    }
  }, [isMobile]);

  if (initializing) {
    return (
      <div
        style={{
          minHeight: '100vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <Spin size="large" />
      </div>
    );
  }

  const effectiveCollapsed = isMobile ? !mobileSidebarOpen : collapsed;

  return (
    <BreadcrumbProvider>
      <AntLayout style={{ height: '100vh', display: 'flex', flexDirection: 'row' }}>
        {!posAppFullscreen && (
          <Sidebar
            collapsed={effectiveCollapsed}
            onCollapse={isMobile ? (v) => setMobileSidebarOpen(!v) : setCollapsed}
            isMobile={isMobile}
            onNavigate={() => {
              if (isMobile) setMobileSidebarOpen(false);
            }}
          />
        )}

        {!posAppFullscreen && isMobile && mobileSidebarOpen && (
          <div
            onClick={() => setMobileSidebarOpen(false)}
            style={{
              position: 'fixed',
              inset: 0,
              background: 'rgba(0,0,0,0.25)',
              zIndex: 99,
            }}
          />
        )}

        <AntLayout style={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
          <Header
            isMobile={isMobile}
            onToggleSidebar={isMobile ? () => setMobileSidebarOpen((v) => !v) : undefined}
          />
          <Content
            style={{
              padding: isMobile ? 12 : 16,
              flex: 1,
              display: 'flex',
              flexDirection: 'column',
              minHeight: 0,
              overflow: 'auto',
            }}
          >
            {!posAppFullscreen && <TrialBanner />}
            {!posAppFullscreen && <GraceBanner />}
            {!posAppFullscreen && <OfflineBanner />}
            {!posAppFullscreen && <SampleDataBanner />}
            {!posAppFullscreen && <Breadcrumb />}
            <Outlet />
          </Content>
        </AntLayout>
      </AntLayout>
    </BreadcrumbProvider>
  );
}

export const Layout = () => (
  <PosFullscreenProvider>
    <LayoutInner />
  </PosFullscreenProvider>
);
